import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { useExplainBenefitCheckMutation } from "store/queries/benefitSelection";
import styled from "styled-components";
import {
  IBenefitSelectionSaveRequest,
  IExplainCheck,
} from "types/BenefitSelection/BenefitSelection";

const Row = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.85rem;
`;

const Failure = styled.span`
  font-size: 0.85rem;
  color: var(--red-600);
`;

const NOT_FOUND = "No check with that RequestID on this account.";
const FAILED = "That check could not be explained.";

const quotedWords = (quoted: boolean | null): string => {
  if (quoted === null) return "unknown";
  return quoted ? "yes" : "no";
};

interface IExplainCheckProps {
  accountId: string;
  /**
   * The configuration on screen, present ONLY while it differs from what is saved. The request
   * carries it so the answer describes the edit somebody is looking at rather than the state they
   * have already moved away from.
   */
  configuration?: IBenefitSelectionSaveRequest;
  check?: IExplainCheck;
  onResult: (check: IExplainCheck | undefined) => void;
}

/**
 * CLE-2792 — "Explain a specific check", at the top of the selection panel.
 *
 * The panel below explains a CONFIGURATION with nothing filled in. This answers the question people
 * actually arrive with — "Ani says this patient's copay is wrong, which step produced it?" — by
 * replaying one stored pVerify response the account has already run.
 *
 * 🚨 NOTHING HERE IS COMPOSED FROM THE VALUES. The header states what the check WAS (payer, plan,
 * date, whether a patient was quoted from it); every sentence about how a benefit was selected comes
 * from the server, which generates it from the engine's own constants. A second, hand-written account
 * of the behaviour is the one that goes wrong first.
 *
 * 🚨 A 404 IS AN ANSWER, NOT AN ERROR. Typing a RequestID the account has never run is the ordinary
 * case — a number from the wrong hospital, a response id pasted by mistake — so it is said inline,
 * beside the box, with no toast: a toast disappears while somebody is still reading the box it is
 * about.
 */
export function ExplainCheck({
  accountId,
  configuration,
  check,
  onResult,
}: IExplainCheckProps) {
  const [requestId, setRequestId] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [explainCheck, { isLoading }] = useExplainBenefitCheckMutation();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = requestId.trim();
    if (!trimmed) return;

    setFailure(null);
    explainCheck({ accountId, pverifyRequestId: trimmed, configuration })
      .unwrap()
      .then((result) => onResult(result.data))
      .catch((error) => {
        onResult(undefined);
        setFailure(error?.status === 404 ? NOT_FOUND : FAILED);
      });
  };

  const clear = () => {
    setRequestId("");
    setFailure(null);
    onResult(undefined);
  };

  return (
    <div
      className="flex flex-column gap-2"
      data-testid="benefitSelection.explainCheck"
    >
      <Row onSubmit={submit}>
        <Field htmlFor="benefitSelection.explainCheck.requestId">
          pVerify RequestID
          <InputText
            id="benefitSelection.explainCheck.requestId"
            value={requestId}
            onChange={(event) => setRequestId(event.target.value)}
            className="p-inputtext-sm"
            style={{ width: "14rem" }}
          />
        </Field>
        <Button
          type="submit"
          label="Explain"
          className="p-button-sm"
          loading={isLoading}
          data-testid="benefitSelection.explainCheck.submit"
        />
        {check && (
          <Button
            type="button"
            label="Clear"
            className="p-button-text p-button-sm"
            onClick={clear}
            data-testid="benefitSelection.explainCheck.clear"
          />
        )}
      </Row>

      {failure && (
        <Failure data-testid="benefitSelection.explainCheck.failure">
          {failure}
        </Failure>
      )}

      {check && (
        <Header data-testid="benefitSelection.explainCheck.header">
          <span className="font-medium">
            {[
              check.check.payerName,
              check.check.planName,
              new Date(check.check.date).toLocaleDateString(),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <span className="text-color-secondary">
            quoted to a patient: {quotedWords(check.check.quotedToPatient)}
            {configuration ? " · using your unsaved changes" : ""}
          </span>
        </Header>
      )}
    </div>
  );
}
